import { useEffect, useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { BarChart3, Users, Eye, MousePointerClick, Monitor, Smartphone, Tablet, ArrowUp, ArrowDown, Minus } from "lucide-react";
import api from "@/lib/api";

const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

const DEVICE_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

export default function AdminAnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [pages, setPages] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [devices, setDevices] = useState({ devices: [], browsers: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [ov, pg, rf, dv] = await Promise.all([
          api.get("/admin/analytics/overview", { params: { days } }),
          api.get("/admin/analytics/pages", { params: { days, limit: 12 } }),
          api.get("/admin/analytics/referrers", { params: { days, limit: 10 } }),
          api.get("/admin/analytics/devices", { params: { days } }),
        ]);
        if (cancelled) return;
        setOverview(ov.data);
        setPages(pg.data.rows || []);
        setReferrers(rf.data.rows || []);
        setDevices(dv.data || { devices: [], browsers: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const series = overview?.series || [];
  const maxView = useMemo(() => Math.max(1, ...series.map((s) => s.pageviews)), [series]);

  return (
    <div className="space-y-6" data-testid="admin-analytics-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-medium tracking-tight flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" /> Analytics
          </h2>
          <p className="text-sm text-stone-500 mt-0.5">First-party visitor stats. No cookies, no third-party trackers.</p>
        </div>
        <div className="inline-flex bg-stone-100 border border-stone-200 rounded-full p-1 gap-1" data-testid="analytics-range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={`text-sm font-semibold px-4 py-1.5 rounded-full transition-colors ${
                days === r.days ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"
              }`}
              data-testid={`analytics-range-${r.days}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !overview ? (
        <div className="text-sm text-stone-500 py-10 text-center">Loading analytics…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard label="Unique visitors" icon={Users} value={overview?.totals?.visitors ?? 0} delta={overview?.deltas?.visitors} />
            <StatCard label="Pageviews" icon={Eye} value={overview?.totals?.pageviews ?? 0} delta={overview?.deltas?.pageviews} />
            <StatCard label="Sessions" icon={MousePointerClick} value={overview?.totals?.sessions ?? 0} />
          </div>

          <div className="border border-stone-200 rounded-2xl p-5 bg-white">
            <div className="text-sm font-medium text-stone-900 mb-3">Pageviews per day</div>
            <svg viewBox={`0 0 ${Math.max(series.length * 24, 24)} 90`} className="w-full h-32">
              {series.map((s, i) => {
                const h = (s.pageviews / maxView) * 80;
                return (
                  <g key={s.date}>
                    <title>{`${s.date}: ${s.pageviews} pageviews, ${s.visitors} visitors`}</title>
                    <rect x={i * 24 + 4} y={86 - h} width={16} height={h} fill="#F59E0B" opacity={0.9} rx={2} />
                  </g>
                );
              })}
            </svg>
            <div className="flex justify-between text-[10px] text-stone-400 mt-1">
              <span>{series[0] ? format(parseISO(series[0].date), "MMM d") : ""}</span>
              <span>{series.at(-1) ? format(parseISO(series.at(-1).date), "MMM d") : ""}</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <RankedList
              title="Top pages"
              testid="analytics-top-pages"
              rows={pages.map((p) => ({ label: p.path, primary: p.pageviews, secondary: `${p.visitors} visitors` }))}
              emptyText="No pageviews recorded yet."
            />
            <RankedList
              title="Top referrers"
              testid="analytics-top-referrers"
              rows={referrers.map((r) => ({ label: r.host, primary: r.visits, secondary: "visits" }))}
              emptyText="No external referrers yet."
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-stone-200 rounded-2xl p-5 bg-white" data-testid="analytics-devices">
              <div className="text-sm font-medium text-stone-900 mb-3">Devices</div>
              <DeviceBars items={devices.devices} iconMap={DEVICE_ICON} />
            </div>
            <div className="border border-stone-200 rounded-2xl p-5 bg-white" data-testid="analytics-browsers">
              <div className="text-sm font-medium text-stone-900 mb-3">Browsers</div>
              <DeviceBars items={devices.browsers} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, icon: Icon, value, delta }) {
  let deltaNode = null;
  if (delta != null) {
    const Arrow = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus;
    const color = delta > 0 ? "text-emerald-700" : delta < 0 ? "text-rose-700" : "text-stone-500";
    deltaNode = (
      <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color}`}>
        <Arrow className="w-3 h-3" /> {Math.abs(delta)}%
      </span>
    );
  }
  return (
    <div className="border border-stone-200 rounded-2xl p-4 bg-white" data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-stone-500" />
        <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">{label}</span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="font-display text-3xl font-bold text-stone-900 tabular-nums">{value.toLocaleString()}</div>
        {deltaNode}
      </div>
      <div className="text-[10px] text-stone-400 mt-1">vs previous period</div>
    </div>
  );
}

function RankedList({ title, rows, emptyText, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.primary));
  return (
    <div className="border border-stone-200 rounded-2xl p-5 bg-white" data-testid={testid}>
      <div className="text-sm font-medium text-stone-900 mb-3">{title}</div>
      {rows.length === 0 ? (
        <div className="text-sm text-stone-500 py-4 text-center">{emptyText}</div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r, i) => (
            <li key={i} className="relative">
              <div className="absolute inset-y-0 left-0 bg-amber-50 rounded-md" style={{ width: `${(r.primary / max) * 100}%` }} />
              <div className="relative flex items-center justify-between px-2 py-1.5 text-sm">
                <span className="truncate text-stone-800 font-mono">{r.label}</span>
                <span className="text-stone-700 tabular-nums shrink-0 ml-3">
                  <strong>{r.primary.toLocaleString()}</strong>{" "}
                  <span className="text-xs text-stone-500">{r.secondary}</span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DeviceBars({ items, iconMap }) {
  const total = items.reduce((a, b) => a + b.count, 0);
  if (total === 0) return <div className="text-sm text-stone-500 py-4 text-center">No data yet.</div>;
  return (
    <ul className="space-y-2">
      {items.map((d, i) => {
        const Icon = iconMap?.[d.label];
        const pct = Math.round((d.count / total) * 100);
        return (
          <li key={i} className="flex items-center gap-3">
            <div className="flex items-center gap-2 w-28 shrink-0">
              {Icon && <Icon className="w-3.5 h-3.5 text-stone-500" />}
              <span className="text-sm capitalize text-stone-800">{d.label}</span>
            </div>
            <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs tabular-nums text-stone-600 w-12 text-right">{pct}%</span>
          </li>
        );
      })}
    </ul>
  );
}
